import { useEffect, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { Npc, Relationship } from '../../types.js';
import type { WorldLaunchHandoff } from '../CreateWorld/draftStorage.js';
import type { WorldState } from '../useSim.js';
import { applyResumePose, syncAquariumVisitorPresence } from './gameSpawn.js';
import type { PersistedRuntimeState } from './types.js';

export function useAquariumVisitorLifecycle(args: {
  enabled: boolean;
  ready: boolean;
  handoff: WorldLaunchHandoff | null;
  runtimeState: PersistedRuntimeState | null;
  npcsRef: MutableRefObject<Npc[]>;
  worldRef: MutableRefObject<WorldState | null>;
  relationshipsRef: MutableRefObject<Map<string, Relationship>>;
  setNpcs: Dispatch<SetStateAction<Npc[]>>;
  dropConversationFor: (npcId: string) => void;
}): void {
  const {
    enabled, ready, handoff, runtimeState, npcsRef, worldRef,
    relationshipsRef, setNpcs, dropConversationFor,
  } = args;
  const appliedPoseRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !ready || !handoff || !runtimeState || !worldRef.current) return;
    const synced = syncAquariumVisitorPresence(
      handoff,
      npcsRef.current,
      worldRef.current,
      runtimeState.clock.inWorldIso,
      relationshipsRef.current,
    );
    const nextIds = new Set(synced.npcs.map((npc) => npc.id));
    npcsRef.current
      .filter((npc) => !nextIds.has(npc.id))
      .forEach((npc) => dropConversationFor(npc.id));
    npcsRef.current = synced.npcs;
    worldRef.current = synced.world;
    setNpcs(synced.npcs);
  }, [
    dropConversationFor, enabled, handoff, npcsRef, ready, relationshipsRef, runtimeState, setNpcs, worldRef,
  ]);

  useEffect(() => {
    const pose = enabled ? runtimeState?.resumePose?.facingAway : undefined;
    if (!pose || !worldRef.current) return;
    const poseKey = pose.join('|');
    if (appliedPoseRef.current === poseKey) return;
    worldRef.current = applyResumePose(worldRef.current, pose);
    appliedPoseRef.current = poseKey;
  }, [enabled, runtimeState, worldRef]);
}
